import { useState, useEffect, useRef, useCallback } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import debounce from 'lodash/debounce';
import { getConfig } from '../AppConfig';

let configured = false;
let placesLibPromise = null;

function getPlacesLibrary() {
  if (!placesLibPromise) {
    if (!configured) {
      setOptions({ key: getConfig().keys.googleAutocomplete, v: 'quarterly' });
      configured = true;
    }
    placesLibPromise = importLibrary('places').catch((err) => {
      placesLibPromise = null;
      throw err;
    });
  }
  return placesLibPromise;
}

export default function useGooglePlaces({
  regionCodes = ['us'],
  debounceMs = 300,
} = {}) {
  const [placePredictions, setPlacePredictions] = useState([]);
  const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] =
    useState(false);
  const [placePredictionsError, setPlacePredictionsError] = useState(false);
  const mounted = useRef(true);
  const generation = useRef(0);
  const regionCodesRef = useRef(regionCodes);
  regionCodesRef.current = regionCodes;

  const debouncedFetch = useRef(
    debounce(async (input, requestGeneration) => {
      const isCurrent = () =>
        mounted.current && generation.current === requestGeneration;
      try {
        // The first query owns its wait for readiness. Loading the library on
        // mount both slowed startup and lost queries made before it resolved.
        const placesLib = await getPlacesLibrary();
        if (!isCurrent()) return;
        const { suggestions } =
          await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            includedRegionCodes: regionCodesRef.current,
          });
        if (!isCurrent()) return;
        setPlacePredictions(
          suggestions
            .filter((s) => s.placePrediction)
            .map((s) => ({
              place_id: s.placePrediction.placeId,
              description: s.placePrediction.text.text,
            }))
        );
      } catch {
        if (!isCurrent()) return;
        setPlacePredictionsError(true);
      } finally {
        if (isCurrent()) setIsPlacePredictionsLoading(false);
      }
    }, debounceMs)
  ).current;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const getPlacePredictions = useCallback(
    (request) => {
      const input =
        typeof request === 'string' ? request : request?.input || '';
      generation.current += 1;
      const { current } = generation;
      debouncedFetch.cancel();
      setPlacePredictions([]);
      setPlacePredictionsError(false);
      setIsPlacePredictionsLoading(Boolean(input));
      if (input) debouncedFetch(input, current);
    },
    [debouncedFetch]
  );

  const fetchPlaceDetails = useCallback(async (placeId) => {
    if (!placeId) return null;
    const placesLib = await getPlacesLibrary();
    const place = new placesLib.Place({ id: placeId });
    await place.fetchFields({
      fields: ['formattedAddress', 'addressComponents', 'location'],
    });
    return place;
  }, []);

  return {
    placePredictions,
    isPlacePredictionsLoading,
    placePredictionsError,
    getPlacePredictions,
    fetchPlaceDetails,
  };
}
